import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LinkButton } from '../components/ui/link-button';
import {
  Dumbbell,
  ArrowLeft,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight,
  Youtube,
  FileText,
  Check,
  Calendar,
  User,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

export const AdminWorkoutManager = ({ user }) => {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  const [showAddExercise, setShowAddExercise] = useState(null);
  const [showAddDay, setShowAddDay] = useState(false);
  const [newExercise, setNewExercise] = useState({
    name: '',
    youtube_url: '',
    instructions: '',
  });
  const [newDay, setNewDay] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserWorkoutPlan(selectedUser.id);
    } else {
      setWorkoutPlan(null);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const participants = response.data.filter(u => u.role === 'participant' && u.is_active);
      setAllUsers(participants);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserWorkoutPlan = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users/${userId}/workout-plan`);
      setWorkoutPlan(response.data);
    } catch (error) {
      console.error('Error fetching workout plan:', error);
      toast.error('Erro ao carregar plano de treinos');
    } finally {
      setLoading(false);
    }
  };

  const addDay = async () => {
    if (!newDay.name.trim()) {
      toast.error('Nome do treino é obrigatório');
      return;
    }

    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/workout-plan/days`, {
        name: newDay.name,
        enabled: true,
        start_date: newDay.start_date || null,
        end_date: newDay.end_date || null,
      });
      toast.success('Treino adicionado!');
      setShowAddDay(false);
      setNewDay({ name: '', start_date: '', end_date: '' });
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao adicionar treino');
    }
  };

  const deleteDay = async (dayId, dayName) => {
    if (!window.confirm(`Excluir treino "${dayName}"? Todos os exercícios serão removidos.`)) return;

    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}`);
      toast.success('Treino excluído');
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao excluir treino');
    }
  };

  const updateDay = async (dayId, data) => {
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}`, data);
      toast.success('Treino atualizado');
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao atualizar treino');
    }
  };

  const toggleDayEnabled = async (dayId, currentEnabled) => {
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}`, {
        enabled: !currentEnabled
      });
      fetchUserWorkoutPlan(selectedUser.id);
      toast.success(currentEnabled ? 'Treino desabilitado' : 'Treino habilitado');
    } catch (error) {
      toast.error('Erro ao atualizar treino');
    }
  };

  const addExercise = async (dayId) => {
    if (!newExercise.name.trim()) {
      toast.error('Nome do exercício é obrigatório');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}/exercises`,
        newExercise
      );
      toast.success('Exercício adicionado!');
      setShowAddExercise(null);
      setNewExercise({ name: '', youtube_url: '', instructions: '' });
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao adicionar exercício');
    }
  };

  const updateExercise = async (dayId, exerciseId, data) => {
    try {
      await axios.patch(
        `${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}/exercises/${exerciseId}`,
        data
      );
      toast.success('Exercício atualizado');
      setEditingExercise(null);
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao atualizar exercício');
    }
  };

  const deleteExercise = async (dayId, exerciseId) => {
    if (!window.confirm('Excluir este exercício?')) return;

    try {
      await axios.delete(
        `${API}/admin/users/${selectedUser.id}/workout-plan/days/${dayId}/exercises/${exerciseId}`
      );
      toast.success('Exercício excluído');
      fetchUserWorkoutPlan(selectedUser.id);
    } catch (error) {
      toast.error('Erro ao excluir exercício');
    }
  };

  if (loading && !selectedUser) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neon-lime border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 md:px-6 pt-4 md:pt-8">
      {/* Header */}
      <motion.div
        className="flex items-center gap-4 mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {selectedUser ? (
          <button
            onClick={() => setSelectedUser(null)}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
        ) : (
          <LinkButton to="/admin" variant="ghost" className="p-2 hover:bg-zinc-800 rounded-lg">
            <ArrowLeft className="w-6 h-6 text-white" />
          </LinkButton>
        )}
        <div>
          <h1 className="font-primary font-black text-2xl md:text-4xl text-white uppercase tracking-tighter">
            {selectedUser ? `Treinos de ${selectedUser.name}` : 'Gerenciar Treinos'}
          </h1>
          <p className="text-zinc-500 text-sm">
            {selectedUser ? 'Configure os treinos deste aluno' : 'Selecione um aluno para gerenciar seus treinos'}
          </p>
        </div>
      </motion.div>

      {/* Lista de alunos ou Gerenciador de treinos */}
      {!selectedUser ? (
        <motion.div
          className="grid gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-neon-lime" />
            <span className="text-zinc-400 text-sm uppercase tracking-wider font-bold">
              Alunos ({allUsers.length})
            </span>
          </div>
          
          {allUsers.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              Nenhum aluno cadastrado
            </div>
          ) : (
            allUsers.map((u) => (
              <motion.button
                key={u.id}
                onClick={() => setSelectedUser(u)}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-neon-lime/30 transition-all text-left w-full"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-bold text-lg">
                  {u.name[0]}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white">{u.name}</h3>
                  <p className="text-zinc-500 text-sm">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Dumbbell className="w-5 h-5" />
                  <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
                </div>
              </motion.button>
            ))
          )}
        </motion.div>
      ) : (
        <>
          {/* Botão adicionar treino */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {!showAddDay ? (
              <Button
                onClick={() => setShowAddDay(true)}
                className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adicionar Treino
              </Button>
            ) : (
              <div className="bg-zinc-900/50 border border-neon-lime/30 rounded-xl p-4">
                <h3 className="font-bold text-white mb-4">Novo Treino</h3>
                <div className="space-y-3">
                  <Input
                    placeholder="Nome do treino (ex: Treino A - Peito)"
                    value={newDay.name}
                    onChange={(e) => setNewDay({ ...newDay, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Data início</label>
                      <Input
                        type="date"
                        value={newDay.start_date}
                        onChange={(e) => setNewDay({ ...newDay, start_date: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Data fim</label>
                      <Input
                        type="date"
                        value={newDay.end_date}
                        onChange={(e) => setNewDay({ ...newDay, end_date: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={addDay} className="bg-neon-lime text-black font-bold">
                      <Check className="w-4 h-4 mr-2" />
                      Salvar
                    </Button>
                    <Button
                      onClick={() => {
                        setShowAddDay(false);
                        setNewDay({ name: '', start_date: '', end_date: '' });
                      }}
                      variant="outline"
                      className="border-zinc-700 text-zinc-400"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Loading */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-neon-lime border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            /* Lista de treinos */
            <div className="space-y-4">
              {workoutPlan?.days?.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <Dumbbell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum treino configurado para este aluno</p>
                  <p className="text-sm mt-2">Clique em "Adicionar Treino" para começar</p>
                </div>
              )}

              {workoutPlan?.days?.map((day) => (
                <motion.div
                  key={day.id}
                  className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Day Header */}
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30 transition-colors"
                    onClick={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDayEnabled(day.id, day.enabled);
                        }}
                        className={`p-1 rounded ${day.enabled ? 'text-neon-lime' : 'text-zinc-600'}`}
                      >
                        {day.enabled ? (
                          <ToggleRight className="w-6 h-6" />
                        ) : (
                          <ToggleLeft className="w-6 h-6" />
                        )}
                      </button>
                      <div>
                        <h3 className={`font-bold ${day.enabled ? 'text-white' : 'text-zinc-500'}`}>
                          {day.name}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                          <span>{day.exercises?.length || 0} exercícios</span>
                          {day.start_date && (
                            <>
                              <span>•</span>
                              <Calendar className="w-3 h-3" />
                              <span>{day.start_date} até {day.end_date || '∞'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteDay(day.id, day.name);
                        }}
                        className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      {expandedDay === day.id ? (
                        <ChevronUp className="w-5 h-5 text-zinc-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-zinc-400" />
                      )}
                    </div>
                  </div>

                  {/* Day Content */}
                  <AnimatePresence>
                    {expandedDay === day.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-zinc-800 overflow-hidden"
                      >
                        <div className="p-4 space-y-3">
                          {/* Exercises */}
                          {day.exercises?.map((exercise, idx) => (
                            <div
                              key={exercise.id}
                              className="bg-zinc-800/50 rounded-lg p-3"
                            >
                              {editingExercise === exercise.id ? (
                                <div className="space-y-2">
                                  <Input
                                    defaultValue={exercise.name}
                                    placeholder="Nome do exercício"
                                    id={`edit-name-${exercise.id}`}
                                    className="bg-zinc-900 border-zinc-700 text-white"
                                  />
                                  <Input
                                    defaultValue={exercise.youtube_url}
                                    placeholder="URL do YouTube"
                                    id={`edit-youtube-${exercise.id}`}
                                    className="bg-zinc-900 border-zinc-700 text-white"
                                  />
                                  <Input
                                    defaultValue={exercise.instructions}
                                    placeholder="Instruções"
                                    id={`edit-instructions-${exercise.id}`}
                                    className="bg-zinc-900 border-zinc-700 text-white"
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        updateExercise(day.id, exercise.id, {
                                          name: document.getElementById(`edit-name-${exercise.id}`).value,
                                          youtube_url: document.getElementById(`edit-youtube-${exercise.id}`).value,
                                          instructions: document.getElementById(`edit-instructions-${exercise.id}`).value,
                                        });
                                      }}
                                      className="bg-neon-lime text-black"
                                    >
                                      <Save className="w-4 h-4 mr-1" />
                                      Salvar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingExercise(null)}
                                      className="border-zinc-700 text-zinc-400"
                                    >
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-zinc-500 text-sm">{idx + 1}.</span>
                                      <h4 className="font-bold text-white">{exercise.name}</h4>
                                    </div>
                                    {exercise.youtube_url && (
                                      <a
                                        href={exercise.youtube_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-red-400 text-sm mt-1 hover:underline"
                                      >
                                        <Youtube className="w-4 h-4" />
                                        Ver vídeo
                                      </a>
                                    )}
                                    {exercise.instructions && (
                                      <p className="text-zinc-400 text-sm mt-1 flex items-start gap-1">
                                        <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        {exercise.instructions}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setEditingExercise(exercise.id)}
                                      className="p-1.5 text-zinc-500 hover:text-neon-lime transition-colors"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => deleteExercise(day.id, exercise.id)}
                                      className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Add Exercise */}
                          {showAddExercise === day.id ? (
                            <div className="bg-zinc-800/50 rounded-lg p-3 border border-neon-lime/30">
                              <div className="space-y-2">
                                <Input
                                  placeholder="Nome do exercício"
                                  value={newExercise.name}
                                  onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                                  className="bg-zinc-900 border-zinc-700 text-white"
                                />
                                <Input
                                  placeholder="URL do YouTube (opcional)"
                                  value={newExercise.youtube_url}
                                  onChange={(e) => setNewExercise({ ...newExercise, youtube_url: e.target.value })}
                                  className="bg-zinc-900 border-zinc-700 text-white"
                                />
                                <Input
                                  placeholder="Instruções (ex: 3x12, 30seg descanso)"
                                  value={newExercise.instructions}
                                  onChange={(e) => setNewExercise({ ...newExercise, instructions: e.target.value })}
                                  className="bg-zinc-900 border-zinc-700 text-white"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => addExercise(day.id)}
                                    className="bg-neon-lime text-black"
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Adicionar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setShowAddExercise(null);
                                      setNewExercise({ name: '', youtube_url: '', instructions: '' });
                                    }}
                                    className="border-zinc-700 text-zinc-400"
                                  >
                                    Cancelar
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowAddExercise(day.id)}
                              className="w-full py-3 border border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:text-neon-lime hover:border-neon-lime/50 transition-colors flex items-center justify-center gap-2"
                            >
                              <Plus className="w-5 h-5" />
                              Adicionar Exercício
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
